import {
  BanknoteArrowDown,
  BanknoteArrowUp,
  BookUser,
  BriefcaseBusiness,
  Building2,
  CalendarClock,
  CalendarDays,
  CarFront,
  CircleDollarSign,
  CircleHelp,
  Contact,
  FileChartColumn,
  FileSignature,
  Gauge,
  Gavel,
  HardHat,
  Library,
  MessageSquare,
  MessagesSquare,
  PenLine,
  Route,
  Scale,
  Settings,
  ShieldCheck,
  Stethoscope,
  Truck,
  UserRound,
  Warehouse,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { hasPermission } from "@/lib/proposal-permissions";
import { rotuloCliente } from "@/lib/rotulos";
import type { PermissionKey, Session } from "@/lib/types";

// TUT-227 — a lateral deixa de ser JSX com `? :` espalhado e vira DADO.
//
// Duas razões, ambas medidas ao desenhar a fatia:
//
// 1. Nenhum teste renderizava o `AppShell` (os contratos dele leem o arquivo
//    como texto), e texto não consegue afirmar "a Consulta enxerga a Visão
//    geral". A guarda que o dono pediu exige interrogar a navegação COM uma
//    sessão — o que só é barato se a navegação for um valor.
// 2. A ordem da lateral virou contrato com a cliente. Como lista literal ela
//    se lê de cima a baixo; como JSX ela era um acidente de escrita.

export interface ItemDeNavegacao {
  to: string;
  label: string;
  icon: LucideIcon;
  /**
   * Escondido quando a sessão não tem NENHUMA destas chaves.
   * Ausente significa "sempre visível" — hoje só a Visão geral e a Agenda.
   * TUT-229: as áreas centrais passaram a exigir a chave `.ver` da área, que
   * todo papel carrega por padrão; o que sobra a uma sessão sem chave nenhuma
   * é o piso (`PISO_DA_LATERAL`).
   */
  exigeAlguma?: PermissionKey[];
  /** Prefixos de rota que mantêm a entrada marcada como ativa. */
  rotasAtivas?: string[];
}

export type GrupoExpansivel =
  | "elaboracao"
  | "consultoria"
  | "cadastros"
  | "financeiro"
  | "transportes";

export interface GrupoDeNavegacao extends Omit<ItemDeNavegacao, "to"> {
  /**
   * A página do próprio grupo. Ausente, o cabeçalho só abre e fecha os
   * filhos — Transportes desde a TUT-330 (dono, 27/09: "se não tem nada
   * interessante e útil que possamos incorporar nele, só expandir e
   * recolher").
   */
  to?: string;
  /** Chave do estado de expansão persistido. */
  chave: GrupoExpansivel;
  filhos: ItemDeNavegacao[];
  /** `end` do link do próprio grupo, quando ele tem página própria. */
  end?: boolean;
}

export type EntradaDeNavegacao = ItemDeNavegacao | GrupoDeNavegacao;

export function ehGrupo(
  entrada: EntradaDeNavegacao,
): entrada is GrupoDeNavegacao {
  return "filhos" in entrada;
}

/**
 * A ORDEM desta lista é o contrato com a cliente. Desde a TUT-538 ela é a do
 * Canva "VISÃO GERAL" (D28, 23/10), mais Relatórios: Visão Geral, Agenda,
 * Clientes, Assistentes, Processos, Agendamento, Perícias, Elaboração,
 * Defesas, Financeiro, Consultoria, Relatórios — e Normas e Transportes logo
 * depois (o destino combinado era o Gerenciamento, que a D30 desfez; a P5
 * segue em aberto com a cliente). Tudo em coluna única, sem bloco de rodapé.
 *
 * TUT-566 — decisão do dono de 30/10 (Canva revisto): Visão geral, Agenda,
 * Cadastros (Clientes, Pessoas, Advogados, Assistentes, Fornecedores, Peritos
 * do juízo — TUT-589: o pai deixou de se chamar Clientes), Processos
 * (item simples), Agendamento, Perícias, Elaboração (Defesas), Financeiro,
 * Consultoria, Relatórios, Normas e Transportes (Frota, Viagens, Locadoras).
 * Prazos e Peças saíram do menu: a lista de prazos é do Agendamento (a rota
 * sai na TUT-568), e a lista de peças é a vista Lista da Elaboração
 * (`/elaboracao-lista`, pelo alternador). As rotas antigas seguem abrindo.
 *
 * Ela recusou o rodapé explicitamente (reunião de 10/09, 31:23): "não gosto da
 * ideia de ter esse pedaço lá embaixo e depois subir assim em cima". A
 * hierarquia mora na ordem.
 *
 * O que SAIU da lateral na TUT-538 continua alcançável, e os endereços antigos
 * redirecionam (`router.tsx`):
 * - o Kanban de processos é a vista Kanban de Processos (`/processos?vista=kanban`);
 * - Documentos é aba do processo e atalho no cabeçalho de Processos;
 * - Assuntos é uma tabela dentro da tela de Clientes ("pode ser só uma
 *   tabelinha", M23).
 *
 * Item de menu só entra quando a tela existe: `app-nav.test.ts` reprova
 * destino que não é tela do roteador. Defesas (TUT-537) entrou quando a tela
 * passou a existir, logo depois de Elaboração.
 */
export const navegacaoPrincipal: EntradaDeNavegacao[] = [
  { to: "/", label: "Visão geral", icon: Gauge },
  // TUT-227 reverte a TUT-175 a pedido da cliente: Agenda volta ao primeiro
  // nível. O motivo original (no primeiro nível ela não dizia DE QUE agenda se
  // tratava) some porque agora ela tem "Agendamento" como vizinho — o par
  // torna a distinção visível em vez de teórica.
  { to: "/agenda", label: "Agenda", icon: CalendarDays },
  {
    // TUT-589 — decisão do dono de 02/11: "Não faz sentido o pai dos cadastros
    // chamar Clientes e ter Fornecedores dentro". Isso REVISA o Canva da cliente
    // de 10/09, que escrevia `CLIENTES` como a porta do grupo (na reunião ela
    // tinha hesitado, 04:35: "eu chamei de cadastro, meu pai chama de cliente,
    // eles chegam e decidem"; a TUT-330 manteve a porta na lista de clientes).
    // Agora o grupo se chama "Cadastros" e é só o pai, SEM página própria — como
    // Transportes: o cabeçalho abre e fecha os filhos. Clientes desceu para
    // dentro dele, no primeiro lugar. `/cadastros` segue redirecionando para a
    // lista de clientes (`/organizacoes`), que continua sendo o cadastro mais
    // usado, e `/assuntos` para a tabela dentro dela (TUT-538, M23).
    label: "Cadastros",
    exigeAlguma: ["cadastros.ver"],
    icon: BookUser,
    chave: "cadastros",
    filhos: [
      { to: "/organizacoes", label: rotuloCliente.plural, icon: Building2, exigeAlguma: ["cadastros.ver"] },
      { to: "/pessoas", label: "Pessoas", icon: UserRound },
      { to: "/advogados", label: "Advogados", icon: Scale },
      // TUT-538 subiu Assistentes ao primeiro nível (D28); a TUT-566 (30/10)
      // o devolve a Clientes. A chave é a do grupo, que o escondia.
      {
        to: "/assistentes",
        label: "Assistentes",
        icon: Contact,
        exigeAlguma: ["cadastros.ver"],
      },
      // TUT-589 — quem presta serviço ou vende ao escritório; a lista abre com
      // `cadastros.ver`, como os outros cadastros.
      {
        to: "/fornecedores",
        label: "Fornecedores",
        icon: Truck,
        exigeAlguma: ["cadastros.ver"],
      },
      // TUT-536 (D10) — os peritos do juízo saíram de Assistentes e ganharam
      // tela própria. TUT-554 (T13-M4, decisão do dono de 28/10): a chave é a
      // MESMA que a API exige para a lista abrir (`ExpertsController`,
      // `cadastros.gerir`). Com a de leitura, a Operação e a Consulta viam o
      // item e tomavam 403. TUT-566: passam de Processos para Clientes.
      {
        to: "/peritos-do-juizo",
        label: "Peritos do juízo",
        icon: Gavel,
        exigeAlguma: ["cadastros.gerir"],
      },
    ],
  },
  // TUT-227 renomeou este item para "Agendamento", lendo a fala de 11:02 como
  // troca de nome. O RASCUNHO dela (10/09) desmente: o menu que ela escreveu
  // tem `PROCESSOS` **e** `AGENDAMENTO` como itens SEPARADOS — Processos é o
  // acervo, Agendamento é a etapa, com quadro próprio. O rótulo volta.
  // TUT-538 — o Kanban de processos é a vista Kanban desta tela, e Perícias
  // subiu ao primeiro nível (D28). TUT-566 — sem Prazos, Peças e Peritos,
  // Processos deixa de ter filhos e vira item simples.
  {
    to: "/processos",
    label: "Processos",
    exigeAlguma: ["processos.ver"],
    icon: BriefcaseBusiness,
  },
  // TUT-230: o rascunho dela tem AGENDAMENTO como item PRÓPRIO, entre
  // PROCESSOS e ELABORAÇÃO — a etapa, não o acervo.
  {
    to: "/agendamento",
    label: "Agendamento",
    icon: CalendarClock,
    exigeAlguma: ["processos.ver"],
    // TUT-568 — a vista Lista (`/agendamento-lista`, pelo alternador) acende o item.
    rotasAtivas: ["/agendamento", "/agendamento-lista"],
  },
  // TUT-538 — no primeiro nível pela D28, entre Agendamento e Elaboração.
  {
    to: "/pericias",
    label: "Perícias",
    icon: Stethoscope,
    exigeAlguma: ["processos.ver"],
  },
  // TUT-566 — grupo com página própria: o quadro. A lista de peças
  // (`/elaboracao-lista`) é a outra vista da mesma etapa e acende o grupo
  // (`rotasAtivas`). Sem `defesas.ver` o grupo vira item simples da página, em
  // vez de sumir (`construirNavegacao`).
  {
    to: "/elaboracao",
    label: "Elaboração",
    icon: PenLine,
    exigeAlguma: ["processos.ver"],
    chave: "elaboracao",
    end: true,
    rotasAtivas: ["/elaboracao", "/elaboracao-lista"],
    filhos: [
      // TUT-537 — Defesas, a biblioteca por documento (D28). A chave
      // `defesas.ver` é de A, O e E (D36): a tela mostra o risco de perda, e a
      // Consulta não a recebe. TUT-566: filha de Elaboração.
      {
        to: "/defesas",
        label: "Defesas",
        icon: ShieldCheck,
        exigeAlguma: ["defesas.ver"],
      },
    ],
  },
  {
    // TUT-251 — o grupo passou a ter destino PRÓPRIO: os dois quadros que ela
    // desenhou. `destinoNoPrimeiroFilho` existia porque não havia tela de
    // Financeiro; agora há, e cair em "Contas a Pagar" esconderia metade.
    to: "/financeiro",
    label: "Financeiro",
    icon: CircleDollarSign,
    chave: "financeiro",
    end: true,
    rotasAtivas: ["/financeiro"],
    filhos: [
      {
        to: "/financeiro/contas-a-pagar",
        label: "Contas a Pagar",
        // TUT-585 — cifrão com seta para cima (sai dinheiro).
        icon: BanknoteArrowUp,
        exigeAlguma: ["contas_a_pagar.gerir"],
      },
      {
        to: "/financeiro/contas-a-receber",
        label: "Contas a Receber",
        // TUT-585 — cifrão com seta para baixo (entra dinheiro).
        icon: BanknoteArrowDown,
        exigeAlguma: ["contas_a_receber.gerir"],
      },
      // TUT-305 — ordem do dono em 23/09, "conforme o canva": Propostas
      // volta ao menu, como filho do Financeiro. Revoga a saída da TUT-250;
      // o quadro dentro de Clientes continua.
      {
        to: "/propostas",
        label: "Propostas",
        icon: FileSignature,
        exigeAlguma: ["propostas.gerir"],
      },
    ],
  },
  {
    to: "/consultoria",
    label: "Consultoria",
    icon: HardHat,
    chave: "consultoria",
    end: true,
    exigeAlguma: ["trabalhos.gerir"],
    filhos: [
      {
        to: "/trabalhos",
        label: "Trabalhos especiais",
        icon: BriefcaseBusiness,
      },
    ],
  },
  // TUT-296b — "Atendimento" saiu da lateral por decisão do dono (25/09): o
  // Atendimento inteiro (chat interno + atendimento de clientes) virou ícone
  // no topo, ao lado do sino (`ChatPanel`, montado em `AppShell.tsx`). A
  // TUT-338 o ícone passou a levar a `/conversas` (Atendimento e Chat numa
  // tela), e `/atendimento` redireciona para lá.
  //
  // Relatórios NÃO é escondido pela chave de escrita: não existe chave de
  // leitura própria para escondê-lo, e escondê-lo por escrita apagaria o menu
  // do Perito, que precisa chegar ali. Ver TUT-229.
  {
    to: "/relatorios",
    label: "Relatórios",
    icon: FileChartColumn,
    exigeAlguma: ["relatorios.ver"],
  },
  {
    // TUT-505 — a consulta normativa (limites, quadros de calor, códigos do
    // eSocial, CAs), só leitura. A paleta Ctrl+K a alcança por AQUI, como
    // todas as telas do menu: não há lista de destinos à parte.
    to: "/normas",
    label: "Normas",
    icon: Library,
    exigeAlguma: ["normas.ver"],
  },
  {
    // TUT-330 — sem página própria. A porta `/transportes` era só o quadro de
    // resumo (o `OperationalHorizon` dentro do botão "?"), e os dois saíram a
    // pedido do dono em 27/09. O cabeçalho abre e fecha os filhos, e o
    // endereço antigo redireciona para Viagens.
    label: "Transportes",
    icon: CarFront,
    chave: "transportes",
    exigeAlguma: ["frota.gerir", "locadoras.gerir"],
    filhos: [
      { to: "/transportes/frota", label: "Frota", icon: CarFront },
      { to: "/transportes/viagens", label: "Viagens", icon: Route },
      // TUT-566 — vinda de Clientes. Ganha a chave que a API exige
      // (`locadoras.gerir`); sem ela ficava visível a quem só tinha
      // `cadastros.ver` e tomava 403 — a mesma correção da TUT-554.
      {
        to: "/locadoras",
        label: "Locadoras",
        icon: Warehouse,
        exigeAlguma: ["locadoras.gerir"],
      },
    ],
  },
];

/**
 * TUT-540 (D30, D37) — a chave da PORTA das Configurações: o item do menu da
 * conta, a paleta e a rota decidem por ela. A API a dá só à Administração e não
 * a deixa conceder (`chaveSoDoAdministrador`).
 */
export const CHAVE_DAS_CONFIGURACOES = "escritorio.configurar" as const;

/** TUT-544 — a única tela do escritório que a pessoa sem grupo alcança. */
export const ROTA_DO_PERFIL = "/perfil" as const;

/**
 * TUT-496 — as telas que NÃO moram na lateral e que a barra de cima alcança:
 * o menu da conta (perfil, Configurações, Ajuda, Feedbacks) e o ícone de
 * Conversas. Eram links escritos à mão no `AppShell`; viraram dado para que a
 * paleta Ctrl+K liste as MESMAS telas que o menu mostra, e não uma cópia que
 * diverge na primeira tela nova (`paleta-de-comandos.test.ts` reprova a
 * divergência nos dois sentidos).
 *
 * Sem `exigeAlguma` de propósito: são de todos — exceto Configurações, que
 * desde a TUT-540 (D30, D37) é só da Administração: o item some do menu e da
 * paleta para quem não tem `escritorio.configurar` (a chave da PORTA; a tela
 * decide por ela, e não pelo papel), e a rota leva à Visão Geral. Dentro da
 * tela, cada seção segue fechada pela própria chave (P25).
 */
export const navegacaoDaConta: ItemDeNavegacao[] = [
  { to: ROTA_DO_PERFIL, label: "Meu perfil", icon: UserRound },
  {
    to: "/configuracoes",
    label: "Configurações",
    icon: Settings,
    exigeAlguma: [CHAVE_DAS_CONFIGURACOES],
  },
  { to: "/ajuda", label: "Ajuda", icon: CircleHelp },
  { to: "/feedbacks", label: "Feedbacks", icon: MessageSquare },
];

/** O menu da conta desta sessão: o que ela alcança, pela chave de cada item. */
export function construirNavegacaoDaConta(session: Session): ItemDeNavegacao[] {
  // TUT-544 — quem está sem grupo só alcança o Meu perfil: Ajuda e Feedbacks
  // levariam de volta a ele.
  if (session.semGrupo) return navegacaoDaConta.filter((item) => item.to === ROTA_DO_PERFIL);
  return navegacaoDaConta.filter((item) => podeVer(session, item));
}

/**
 * A rota do ícone de Conversas. O `ChatPanelButton` lê DAQUI (e não repete a
 * string), e a paleta Ctrl+K lê `navegacaoDoCabecalho`: o ícone e a paleta não
 * têm como divergir (`paleta-de-comandos.test.ts` reprova o literal no botão).
 */
export const ROTA_DE_CONVERSAS = "/conversas" as const;

/** O ícone de Conversas da barra de cima (P27): Atendimento e Chat numa tela. */
export const navegacaoDoCabecalho: ItemDeNavegacao[] = [
  { to: ROTA_DE_CONVERSAS, label: "Conversas", icon: MessagesSquare },
];

/** O piso da lateral: servido a qualquer sessão, com ou sem permissão. */
export const PISO_DA_LATERAL = "/";

/**
 * As rotas que mantêm um grupo aberto: a porta dele mais a de cada filho.
 *
 * Deriva do MESMO modelo que a lateral renderiza — se as duas listas fossem
 * escritas à mão, um filho novo abriria a tela sem abrir o grupo, e o item
 * ficaria escondido justamente na página em que ele é o assunto.
 */
export function rotasDoGrupo(chave: GrupoExpansivel): string[] {
  const grupo = navegacaoPrincipal.find(
    (entrada): entrada is GrupoDeNavegacao =>
      ehGrupo(entrada) && entrada.chave === chave,
  );
  if (!grupo) return [];
  const portas = grupo.rotasAtivas ?? (grupo.to ? [grupo.to] : []);
  return [...portas, ...grupo.filhos.map((filho) => filho.to)];
}

function podeVer(session: Session, entrada: Pick<ItemDeNavegacao, "exigeAlguma">): boolean {
  if (!entrada.exigeAlguma || entrada.exigeAlguma.length === 0) return true;
  return entrada.exigeAlguma.some((chave) => hasPermission(session, chave));
}

/**
 * A lateral que esta sessão enxerga, na ordem do contrato.
 *
 * O piso é decisão do dono (10/09): "garantir que o grupo mais simples tenha
 * por default visualização no mínimo da visão geral". Uma lateral vazia não
 * comunica "você não tem acesso" — comunica "o sistema quebrou", e cairia
 * justamente sobre o papel Consulta, que hoje não recebe permissão nenhuma.
 */
export function construirNavegacao(session: Session): EntradaDeNavegacao[] {
  // TUT-544 — a pessoa interna sem grupo não tem lateral: nem o piso. O que ela
  // vê é o Meu perfil, pelo menu da conta, com o aviso de que falta um grupo.
  if (session.semGrupo) return [];
  const visiveis = navegacaoPrincipal.flatMap<EntradaDeNavegacao>((entrada) => {
    if (!podeVer(session, entrada)) return [];

    if (!ehGrupo(entrada)) return [entrada];

    const filhos = entrada.filhos.filter((filho) => podeVer(session, filho));
    if (filhos.length === 0) {
      // TUT-566 — o grupo cuja PÁGINA tem chave própria (Elaboração) vira o
      // item simples dela, em vez de sumir: a Consulta tem `processos.ver` e
      // não tem `defesas.ver`, e perderia a etapa inteira. O Financeiro tem
      // página, mas sem chave própria — quem decide é o filho, e sem filho
      // visível ele some. Sem página, o pai não leva a lugar nenhum: é ruído,
      // não navegação.
      if (!entrada.to || !entrada.exigeAlguma) return [];
      // `rotasAtivas` vai junto: sem ela o link só acende em /elaboracao, e a
      // vista Lista (/elaboracao-lista) ficaria sem item aceso.
      const { to, label, icon, exigeAlguma, rotasAtivas } = entrada;
      return [{ to, label, icon, exigeAlguma, rotasAtivas }];
    }

    // TUT-251 — o `destinoNoPrimeiroFilho` morava aqui porque o Financeiro
    // era o único grupo sem página própria. Agora ele tem uma, e nenhum grupo
    // usava a exceção: ela saiu junto, em vez de ficar de mecanismo à espera
    // de um caso que o próximo a ler teria de adivinhar.
    return [{ ...entrada, filhos }];
  });

  // A trava do piso vive AQUI, e não na confiança de que a Visão geral nunca
  // ganhe permissão: se alguém a permissionar por engano, ela volta mesmo assim.
  if (!visiveis.some((entrada) => entrada.to === PISO_DA_LATERAL)) {
    const piso = navegacaoPrincipal.find(
      (entrada) => entrada.to === PISO_DA_LATERAL,
    );
    if (piso) return [piso, ...visiveis];
  }

  return visiveis;
}
